import React, { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { FiMail, FiThumbsUp, FiCalendar, FiAward } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const rankStyle = (rank) => {
  if (rank === 1) return { bg: "bg-yellow-100", text: "text-yellow-600", label: "🥇" };
  if (rank === 2) return { bg: "bg-gray-100",   text: "text-gray-500",   label: "🥈" };
  if (rank === 3) return { bg: "bg-orange-100", text: "text-orange-500", label: "🥉" };
  return { bg: "bg-blue-50", text: "text-blue-400", label: `#${rank}` };
};

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const WeeklyLeaderboard = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [loadingUser, setLoadingUser] = useState(null);
  const [positiveReplyLoading, setPositiveReplyLoading] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/auth/dashboard/weekly-leaderboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
        }),
      });
      const result = await res.json();
      if (res.ok && result.status) {
        setLeaders(result.data.topReferrers || []);
        toast.success("Leaderboard loaded");
      } else {
        toast.error(result.message || "Failed to fetch leaderboard");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMail = async (user) => {
    setLoadingUser(user.username);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/auth/email/weekly-leaderboard-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email: user.email,
          username: user.username,
          reffercount: user.reffercount || "0",
          reward: user.reward || "0",
        }),
      });
      const result = await res.json();
      if (res.ok && result.status) {
        toast.success(`Mail sent to ${user.email}`);
      } else {
        toast.error(result.message || "Failed to send mail");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoadingUser(null);
    }
  };

  const handlePositiveReply = async (user) => {
    setPositiveReplyLoading(user.username);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/auth/email/positive-replay`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, username: user.username }),
      });
      const result = await res.json();
      if (res.ok && result.status) {
        toast.success("Positive reply sent!");
      } else {
        toast.error(result.message || "Failed to send positive reply");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setPositiveReplyLoading(null);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Page heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <FiAward size={20} className="text-yellow-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Weekly Leaderboard</h2>
              <p className="text-sm text-gray-400 mt-0.5">Top referrers for a selected week</p>
            </div>
          </div>

          {/* Filter card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  <FiCalendar className="inline mr-1" size={11} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  <FiCalendar className="inline mr-1" size={11} />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition"
              >
                {loading ? <><Spinner /> Loading...</> : "Load Leaderboard"}
              </button>
            </form>
          </div>

          {/* Table / empty state */}
          {leaders.length === 0 ? (
            !loading && (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <div className="text-4xl mb-3">🏆</div>
                <p className="text-gray-400 text-sm">Select a date range and load the leaderboard.</p>
              </div>
            )
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Top Referrers</h3>
                <span className="text-xs text-gray-400">{leaders.length} users</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Rank</th>
                      <th className="px-5 py-3">Username</th>
                      <th className="px-5 py-3 hidden md:table-cell">Email</th>
                      <th className="px-5 py-3 text-right">Coins</th>
                      <th className="px-5 py-3 text-right hidden sm:table-cell">Referrals</th>
                      <th className="px-5 py-3 text-right hidden sm:table-cell">Reward</th>
                      <th className="px-5 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {leaders.map((user, index) => {
                      const rank = index + 1;
                      const rs = rankStyle(rank);
                      return (
                        <tr key={user.id || index} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${rs.bg} ${rs.text}`}>
                              {rs.label}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-800">{user.username || "—"}</p>
                            <p className="text-xs text-gray-400 md:hidden">{user.email}</p>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{user.email}</td>
                          <td className="px-5 py-3.5 text-right font-semibold text-gray-700">
                            {parseFloat(user.coin || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 text-right text-gray-600 hidden sm:table-cell">
                            {user.reffercount || 0}
                          </td>
                          <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                            <span className="inline-block bg-green-50 text-green-600 text-xs font-medium px-2 py-0.5 rounded-full">
                              {user.reward || 0}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleSendMail(user)}
                                disabled={loadingUser === user.username}
                                title="Send Email"
                                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                {loadingUser === user.username ? <Spinner /> : <FiMail size={13} />}
                                <span className="hidden sm:inline">
                                  {loadingUser === user.username ? "Sending..." : "Send Mail"}
                                </span>
                              </button>
                              <button
                                onClick={() => handlePositiveReply(user)}
                                disabled={positiveReplyLoading === user.username}
                                title="Positive Reply"
                                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                {positiveReplyLoading === user.username ? <Spinner /> : <FiThumbsUp size={13} />}
                                <span className="hidden sm:inline">
                                  {positiveReplyLoading === user.username ? "Sending..." : "Positive"}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WeeklyLeaderboard;
